import type { ConversationDocument, EditBatch } from '@corner/shared';
import type { CoTStepData } from '../components/app/cot/types';

export type VersionTimelineEntry =
  | {
      kind: 'draft';
      version: number | string;
      documentId: string;
      createdAt: string;
      title: string;
      source: 'draft-stream' | 'import';
    }
  | {
      kind: 'edit_batch';
      version: number | string;
      documentId: string;
      batchId: string;
      label: string;
      createdAt: string;
      acceptedCount: number;
      declinedCount: number;
      origin: 'html_edit' | 'clause_flags' | 'plan_doc_edits';
    };

type TimelineCotStep = Pick<CoTStepData, 'title' | 'description' | 'editBatch'>;

function versionNumber(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/^v/i, '').trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function docIdFromBatch(batch: EditBatch): string | null {
  const fileId = batch.resultingVersion?.fileId;
  if (typeof fileId === 'string' && fileId.startsWith('doc-')) return fileId.slice(4);
  const docId = batch.resultingVersion?.docId;
  if (typeof docId === 'string' && docId.startsWith('doc-')) return docId.slice(4);
  return batch.baseDocumentId ?? null;
}

function batchVersion(batch: EditBatch): number | string | null {
  return batch.resultingVersion?.version ?? batch.baseDocumentVersion ?? null;
}

function batchMatchesDocument(batch: EditBatch, doc: ConversationDocument): boolean {
  const byDocId = docIdFromBatch(batch);
  const byVersion = versionNumber(batchVersion(batch));
  return Boolean(
    (byDocId && byDocId === doc.id) ||
    (byVersion != null && byVersion === doc.version),
  );
}

function inferOrigin(batch: EditBatch, step?: TimelineCotStep): 'html_edit' | 'clause_flags' | 'plan_doc_edits' {
  const text = [
    step?.title,
    step?.description,
    batch.draftTitle,
    batch.sourceFilename,
    ...batch.edits.map((edit) => edit.description),
  ].filter(Boolean).join(' ').toLowerCase();
  if (text.includes('clause') || text.includes('flag')) return 'clause_flags';
  if (batch.applyMode === 'conversation_html_revision') return 'html_edit';
  return 'plan_doc_edits';
}

function labelForBatch(batch: EditBatch, step?: TimelineCotStep): string {
  const firstEdit = batch.edits.find((edit) => edit.description?.trim());
  if (firstEdit?.description?.trim()) {
    const extra = Math.max(0, batch.edits.length - 1);
    return extra > 0 ? `${firstEdit.description.trim()} + ${extra} more` : firstEdit.description.trim();
  }
  if (step?.description?.trim()) return step.description.trim();
  if (step?.title?.trim()) return step.title.trim();
  if (batch.draftTitle?.trim()) return `Update ${batch.draftTitle.trim()}`;
  return 'Applied document edits';
}

export function buildVersionTimeline(opts: {
  conversationId: string;
  documents: ConversationDocument[];
  cotSteps: TimelineCotStep[];
}): VersionTimelineEntry[] {
  const documents = [...(opts.documents ?? [])]
    .filter((doc) => doc && doc.id && Number.isFinite(Number(doc.version)))
    .sort((a, b) => {
      const versionDelta = Number(a.version) - Number(b.version);
      if (versionDelta !== 0) return versionDelta;
      return String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''));
    });
  const batchSteps = (opts.cotSteps ?? [])
    .filter((step): step is TimelineCotStep & { editBatch: EditBatch } => Boolean(step?.editBatch));

  return documents.map((doc, index): VersionTimelineEntry => {
    const matchingStep = batchSteps.find((step) => batchMatchesDocument(step.editBatch, doc));
    if (matchingStep?.editBatch && index > 0) {
      const batch = matchingStep.editBatch;
      return {
        kind: 'edit_batch',
        version: batch.resultingVersion?.version ?? doc.version,
        documentId: docIdFromBatch(batch) ?? doc.id,
        batchId: batch.id,
        label: labelForBatch(batch, matchingStep),
        createdAt: doc.updatedAt ?? doc.createdAt,
        acceptedCount: batch.edits.filter((edit) => edit.status === 'accepted').length,
        declinedCount: batch.edits.filter((edit) => edit.status === 'rejected' || edit.status === 'superseded').length,
        origin: inferOrigin(batch, matchingStep),
      };
    }
    return {
      kind: 'draft',
      version: doc.version,
      documentId: doc.id,
      createdAt: doc.createdAt,
      title: doc.title || `Version ${doc.version}`,
      source: doc.originPrompt ? 'draft-stream' : 'import',
    };
  });
}
